/**
 * Helpers for presenting a Phase II screening match.
 *
 * Grounded in the reference payloads held with the design spec
 * (TTL-DES-LNX2-002). Real LexisNexis data is ragged: partial dates, duplicated
 * nationalities, ten renderings of the same place of birth, and aliases that are
 * numeric codes. These functions keep that mess out of the components.
 */

/** The six segment categories, in the order they are always displayed. */
export const SEGMENT_ORDER = [
  "pep",
  "sanctions",
  "adverse_media",
  "enforcements",
  "soes",
  "other",
] as const;

export type SegmentKey = (typeof SEGMENT_ORDER)[number];

export const SEGMENT_LABELS: Record<string, string> = {
  pep: "PEP",
  sanctions: "Sanctions",
  adverse_media: "Adverse Media",
  enforcements: "Enforcements",
  soes: "SOE",
  other: "Other",
};

/**
 * Counts for all six categories, whichever the payload happens to carry.
 *
 * A zero is information: "no sanctions exposure" is a finding, and hiding the
 * category makes the reviewer prove a negative.
 */
export const segmentCounts = (segments: Record<string, any[]> | undefined) =>
  SEGMENT_ORDER.map((key) => ({
    key,
    label: SEGMENT_LABELS[key],
    count: Array.isArray(segments?.[key]) ? segments![key].length : 0,
  }));

/**
 * `relationships[].segments` is a comma joined string using a different
 * vocabulary from the segment keys, e.g. "Enforcement,PEP,Sanction".
 */
const RELATIONSHIP_SEGMENT_MAP: Record<string, SegmentKey> = {
  pep: "pep",
  sanction: "sanctions",
  sanctions: "sanctions",
  adversemedia: "adverse_media",
  enforcement: "enforcements",
  enforcements: "enforcements",
  soe: "soes",
  other: "other",
};

export const relationshipSegmentKeys = (value: unknown): SegmentKey[] => {
  if (typeof value !== "string" || !value.trim()) return [];
  const keys = value
    .split(",")
    .map((token) => RELATIONSHIP_SEGMENT_MAP[token.trim().toLowerCase()])
    .filter(Boolean) as SegmentKey[];
  return SEGMENT_ORDER.filter((key) => keys.includes(key));
};

/**
 * A match the provider flagged for exclusion: a non-empty `exclude_reason`,
 * meaning the tenant's screening preferences filtered it out. Such matches do
 * not count toward the record status or appear on the PDF, so they are listed
 * apart, are not counted, and carry no decision controls (LNXR-S01).
 */
export const isExcludedMatch = (match: any) =>
  Array.isArray(match?.exclude_reason) && match.exclude_reason.length > 0;

/** True Match first, then Unknown, then False Positive (LNXR-S02). */
const DECISION_RANK: Record<string, number> = {
  true_match: 0,
  unknown: 1,
  false_positive: 2,
};

const decisionRank = (status: unknown) =>
  DECISION_RANK[String(status ?? "unknown")] ?? DECISION_RANK.unknown;

const scoreOf = (value: unknown) => {
  const score = Number(value);
  return Number.isFinite(score) ? score : 0;
};

/**
 * The work queue order: by decision, True Match >> Unknown >> False Positive,
 * then by LexisNexis score, highest first. Stable, so matches with the same
 * decision and score keep their payload order across refreshes.
 *
 * The accessors let monitoring, which holds the decision apart from the body,
 * share the rule with the screening check.
 */
export const orderByDecision = <T>(
  matches: T[],
  getStatus: (match: T) => unknown = (match: any) => match?.status,
  getScore: (match: T) => unknown = (match: any) => match?.entityScore,
): T[] =>
  matches
    .map((match, index) => ({ match, index }))
    .sort(
      (a, b) =>
        decisionRank(getStatus(a.match)) - decisionRank(getStatus(b.match)) ||
        scoreOf(getScore(b.match)) - scoreOf(getScore(a.match)) ||
        a.index - b.index,
    )
    .map(({ match }) => match);

/** By LexisNexis score alone, highest first. Excluded matches carry no decision. */
export const orderByScore = <T>(
  matches: T[],
  getScore: (match: T) => unknown = (match: any) => match?.entityScore,
): T[] =>
  matches
    .map((match, index) => ({ match, index }))
    .sort(
      (a, b) =>
        scoreOf(getScore(b.match)) - scoreOf(getScore(a.match)) ||
        a.index - b.index,
    )
    .map(({ match }) => match);

/** Lower case, strip accents and punctuation, split into comparable tokens. */
export const nameTokens = (value: unknown): string[] =>
  String(value ?? "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9\s]/g, " ")
    .split(/\s+/)
    .filter(Boolean);

/**
 * How much of the searched name appears in a candidate name, from 0 to 1.
 *
 * Deliberately asymmetric: a candidate carrying extra tokens is not penalised,
 * because "Donald John Trump Sr" is a good match for a search on "Donald Trump".
 */
export const nameOverlapScore = (searched: unknown, candidate: unknown) => {
  const searchedTokens = nameTokens(searched);
  if (!searchedTokens.length) return 0;
  const candidateTokens = new Set(nameTokens(candidate));
  const hits = searchedTokens.filter((token) => candidateTokens.has(token));
  return hits.length / searchedTokens.length;
};

/** Alias types that are never shown: numeric provider codes, not names. */
const EXCLUDED_ALIAS_TYPES = ["ccc"];

const isExcludedAlias = (alias: any) =>
  EXCLUDED_ALIAS_TYPES.includes(String(alias?.type ?? "").toLowerCase());

export const isLatinAlias = (alias: any) =>
  String(alias?.script_type ?? "").toLowerCase() === "basic latin";

export interface DisplayAlias {
  full: string;
  type: string | null;
  scriptType: string | null;
}

const toDisplayAlias = (alias: any): DisplayAlias => {
  const rawType = String(alias?.type ?? "").trim();
  return {
    full: String(alias?.name?.full ?? "").trim(),
    // the provider uses "None" where it means "no alias type recorded"
    type: rawType && rawType.toLowerCase() !== "none" ? rawType : null,
    scriptType: alias?.script_type ?? null,
  };
};

/**
 * Aliases split into Latin names and transliterations, with provider codes
 * dropped. Trump carries four numeric `CCC` entries and eight non-Latin
 * renderings; Putin carries nine non-Latin renderings.
 */
export const groupAliases = (akas: any[] | undefined) => {
  const usable = (akas || []).filter(
    (alias) => !isExcludedAlias(alias) && String(alias?.name?.full ?? "").trim()
  );
  return {
    aliases: usable.filter(isLatinAlias).map(toDisplayAlias),
    transliterations: usable.filter((a) => !isLatinAlias(a)).map(toDisplayAlias),
  };
};

/**
 * The alias a match was probably reached through, or null where the primary
 * name already accounts for it.
 *
 * The provider does not tell us which name matched, so this is inferred. Only
 * Latin script aliases are considered, because a Cyrillic alias cannot have
 * matched a Latin search string. An alias must beat the primary name outright,
 * so a match on the primary name never shows an alias line.
 *
 * This is the design's one piece of guesswork. Passing `BestNameScore` through
 * from the provider would remove it: see TTL-DES-LNX2-002 section 8.
 */
export const resolveMatchedAlias = (
  searchedName: unknown,
  matched: any
): DisplayAlias | null => {
  const searchedTokens = nameTokens(searchedName);
  if (!searchedTokens.length) return null;

  const primaryScore = nameOverlapScore(searchedName, matched?.name?.full);
  if (primaryScore >= 1) return null;

  const candidates = groupAliases(matched?.aka)
    .aliases.map((alias) => ({
      alias,
      score: nameOverlapScore(searchedName, alias.full),
    }))
    .filter(({ score }) => score > primaryScore)
    .sort(
      (a, b) =>
        b.score - a.score ||
        // prefer a named alias type over an unlabelled one, then the shortest
        Number(Boolean(b.alias.type)) - Number(Boolean(a.alias.type)) ||
        a.alias.full.length - b.alias.full.length
    );

  return candidates.length ? candidates[0].alias : null;
};

/**
 * Dates arrive as `YYYY-MM-DD` but a zero month and day means the provider only
 * knows the year, e.g. "1952-00-00". Formatting that as a date invents precision
 * the source does not have.
 */
export const formatScreeningDate = (value: unknown): string => {
  const raw = String(value ?? "").trim();
  if (!raw) return "";

  const parts = raw.split("-");
  if (parts.length === 3) {
    const [year, month, day] = parts;
    if (month === "00" && day === "00") return year;
    if (day === "00") return `${month}/${year}`;
    return `${day}/${month}/${year}`;
  }
  return raw;
};

/**
 * Newest first, by an ISO `date_modified`.
 *
 * Sanctions, enforcements, adverse media and sources all carry a real ISO
 * timestamp, so these sort cleanly. Entries with no date sink to the bottom in
 * payload order rather than being treated as very old.
 */
export const byDateModifiedDesc = <T extends { date_modified?: string }>(
  entries: T[] | undefined
): T[] => {
  const list = [...(entries || [])];
  return list
    .map((entry, index) => ({ entry, index }))
    .sort((a, b) => {
      const aTime = Date.parse(a.entry?.date_modified ?? "");
      const bTime = Date.parse(b.entry?.date_modified ?? "");
      const aValid = !Number.isNaN(aTime);
      const bValid = !Number.isNaN(bTime);
      if (aValid && bValid) return bTime - aTime || a.index - b.index;
      if (aValid) return -1;
      if (bValid) return 1;
      return a.index - b.index;
    })
    .map(({ entry }) => entry);
};

/**
 * Comments, newest first where the date can be read.
 *
 * Their `date` is a display string, and only about two thirds of them are a
 * full "August 21, 2025". The rest are month-only ("September 2023") or absent.
 * Datable comments are ordered; the remainder keep payload order at the end,
 * rather than sorting the whole list on a signal a third of it does not carry.
 */
const parseCommentDate = (value: unknown): number => {
  const raw = String(value ?? "").trim();
  if (!raw) return NaN;
  const parsed = Date.parse(raw);
  return Number.isNaN(parsed) ? NaN : parsed;
};

export const orderComments = <T extends { date?: string }>(
  comments: T[] | undefined
): T[] => {
  const list = [...(comments || [])];
  const dated = list
    .map((comment, index) => ({ comment, index, at: parseCommentDate(comment?.date) }))
    .filter(({ at }) => !Number.isNaN(at))
    .sort((a, b) => b.at - a.at || a.index - b.index)
    .map(({ comment }) => comment);
  const undated = list.filter((comment) =>
    Number.isNaN(parseCommentDate(comment?.date))
  );
  return [...dated, ...undated];
};

/**
 * Whether a submitted field actually carries anything to compare against.
 *
 * Only entity type, name, date of birth and gender are ever submitted, so the
 * remaining comparison rows have nothing to assess and must not report a match.
 */
export const hasValue = (value: unknown): boolean => {
  if (value === null || value === undefined) return false;
  if (Array.isArray(value)) return value.length > 0;
  if (typeof value === "object") return Object.keys(value).length > 0;
  return String(value).trim() !== "";
};

/** Distinct, non-empty values in payload order. */
export const uniqueValues = (values: unknown): string[] => {
  const list = Array.isArray(values) ? values : values == null ? [] : [values];
  const seen = new Set<string>();
  const out: string[] = [];
  list.forEach((value) => {
    const text = String(value ?? "").trim();
    if (!text || seen.has(text)) return;
    seen.add(text);
    out.push(text);
  });
  return out;
};

/**
 * Dates of birth, most complete first, so a full date is shown ahead of a
 * year-only one for the same subject.
 */
export const datesOfBirth = (value: unknown): string[] =>
  uniqueValues(value)
    .map((raw) => ({ raw, formatted: formatScreeningDate(raw) }))
    .sort((a, b) => b.formatted.length - a.formatted.length)
    .map(({ formatted }) => formatted)
    .filter(Boolean)
    .filter((value, index, all) => all.indexOf(value) === index);

/**
 * Conflicts, taken from `profile_details.match`.
 *
 * The map is built from the provider's conflict flags, so `true` means "no
 * conflict detected" and absent data also reads as `true`. It is only ever
 * meaningful inverted: a `false` is a positive reason to discount a match.
 */
export const CONFLICT_LABELS: Record<string, string> = {
  entity_type: "Entity type",
  name: "Name",
  date_of_birth: "Date of birth",
  gender: "Gender",
  ids: "Identifiers",
  addresses: "Addresses",
  nationality: "Nationality",
};

export const matchConflicts = (matchFlags: Record<string, any> | undefined) =>
  Object.entries(matchFlags || {})
    .filter(([, ok]) => ok === false)
    .map(([key]) => CONFLICT_LABELS[key] || key);
